import { Controller } from "@hotwired/stimulus"
// Offer-detail hero as stories: one progress segment per slide across the
// top, the active one filling over the slide's play time before the next
// plays; a tap on the right half advances, on the left half rewinds; a
// segment jumps. Slides come from data-gallery-urls-value, each an object:
// { image } plays `duration` (three seconds) as a still, { video, poster }
// plays the file itself, muted, for its own length. Wraps around at both
// ends. The clock stops while the tab is hidden so a slide never "plays"
// unseen.
export default class extends Controller {
  static targets = ["main", "video", "dot", "fill", "credit", "creditLink", "creditAvatar", "creditInitial", "creditHandle", "creditCaption"]
  static values = { urls: Array, index: { type: Number, default: 0 }, duration: { type: Number, default: 3000 } }

  connect() {
    this.onVisibility = () => (document.hidden ? this.stop() : this.play())
    document.addEventListener("visibilitychange", this.onVisibility)
    this.play()
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.onVisibility)
    this.stop()
  }

  prev() { this.go(this.indexValue - 1) }
  next() { this.go(this.indexValue + 1) }
  select(event) { this.go(Number(event.currentTarget.dataset.galleryIndex)) }

  go(i) {
    const n = this.urlsValue.length
    if (n === 0) return
    this.indexValue = ((i % n) + n) % n
    this.play()
  }

  // Paint the slide and (re)start its clock: past segments full, future ones
  // empty, the current one sliding from 0 to 100% over the slide's length.
  play() {
    this.stop()
    const slide = this.slide(this.indexValue)
    if (!slide) return
    this.dotTargets.forEach((d, j) => { d.dataset.active = (j === this.indexValue) })
    this.fillTargets.forEach((f, j) => {
      f.style.transition = "none"
      f.style.width = j < this.indexValue ? "100%" : "0%"
    })
    slide.video ? this.playVideo(slide) : this.playImage(slide)
    this.sign(slide)
  }

  // The poster's signature over the bottom gradient — round avatar (or the
  // handle's initial when there is none) beside the handle, two lines of
  // caption under them, linking to the post. A photo slide has none: hidden.
  sign(slide) {
    if (!this.hasCreditTarget) return
    const show = !!slide.handle
    this.creditTarget.classList.toggle("hidden", !show)
    if (!show) return
    this.creditLinkTarget.href = slide.link || "#"
    this.creditHandleTarget.textContent = slide.handle
    this.creditCaptionTarget.textContent = slide.caption || ""
    this.creditAvatarTarget.classList.toggle("hidden", !slide.avatar)
    this.creditInitialTarget.classList.toggle("hidden", !!slide.avatar)
    if (slide.avatar) this.creditAvatarTarget.src = slide.avatar
    else this.creditInitialTarget.textContent = slide.handle.replace(/^@/, "").charAt(0)
  }

  playImage(slide) {
    if (this.hasVideoTarget) {
      this.videoTarget.pause()
      this.videoTarget.classList.add("hidden")
    }
    if (this.hasMainTarget) {
      this.mainTarget.src = slide.image
      this.mainTarget.classList.remove("hidden")
    }
    this.fill(this.durationValue)
    if (this.urlsValue.length > 1) this.timer = setTimeout(() => this.next(), this.durationValue)
  }

  // The file plays itself; its metadata says how long the segment takes and
  // its end turns the page. A file that will not load moves on like a still.
  playVideo(slide) {
    if (this.hasMainTarget) this.mainTarget.classList.add("hidden")
    if (!this.hasVideoTarget) return
    const video = this.videoTarget
    video.classList.remove("hidden")
    video.onloadedmetadata = () => this.fill(video.duration * 1000)
    video.onended = () => this.next()
    video.onerror = () => { this.timer = setTimeout(() => this.next(), this.durationValue) }
    if (video.getAttribute("src") !== slide.video) {
      video.poster = slide.poster || ""
      video.src = slide.video
    } else {
      video.currentTime = 0
      if (video.readyState >= 1) this.fill(video.duration * 1000)
    }
    video.play().catch(() => {})
  }

  fill(ms) {
    const current = this.fillTargets[this.indexValue]
    if (!current) return
    void current.offsetWidth // commit the reset before animating
    current.style.transition = `width ${ms}ms linear`
    current.style.width = "100%"
  }

  slide(i) {
    const s = this.urlsValue[i]
    return typeof s === "string" ? { image: s } : s
  }

  stop() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
  }
}
